import { useLocation } from 'react-router-dom';
import { siteInfo } from '@/mocks/siteInfo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
}: SEOHeadProps) {
  const location = useLocation();
  const pageTitle = title ? `${title} - ${siteInfo.name}` : `${siteInfo.name} | 51吃瓜网站与吃瓜网站线路一 | 网红吃瓜网站每日大赛黑料实时追踪`;
  const pageDescription = description || siteInfo.description;
  const pageKeywords = keywords || siteInfo.keywords;
  const pageUrl = canonicalUrl || `${siteInfo.url}${location.pathname}`;

  const baseSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        foundingDate: siteInfo.foundingYear,
        contactPoint: {
          '@type': 'ContactPoint',
          email: siteInfo.contact.email,
          contactType: 'customer service',
        },
      },
      {
        '@type': 'WebSite',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        potentialAction: {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        url: pageUrl,
        name: pageTitle,
        description: pageDescription,
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: siteInfo.url,
        },
      },
    ],
  };

  let mergedSchema = baseSchema;
  if (schema) {
    const schemas = Array.isArray(schema) ? schema : [schema];
    mergedSchema = {
      ...baseSchema,
      '@graph': [...(baseSchema['@graph'] as Array<Record<string, unknown>>), ...schemas],
    };
  }

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteInfo.name} />
      <meta property="og:locale" content="zh_CN" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      <script type="application/ld+json">
        {JSON.stringify(mergedSchema)}
      </script>
    </>
  );
}